import { useEffect } from 'react';
import { loadScript } from '../../../../helpers/ScriptLoader';

const BASE_URL = '//go.gettimely.com';
const MUNCHKIN_ID = '304-RPU-875';
const MARKETO_FORM_ID = 1184;

type ReferAFriendProps = {
  email: string;
  firstName: string;
  referralId: string;
};

type MktoFormInstance = {
  vals: (values: {
    Email?: string;
    FirstName?: string;
    AP_Referrer_ID__c_contact?: string;
  }) => void;
  submit: () => void;
  onSuccess: (
    callback: (_values: unknown, _followUpUrl: string) => boolean,
  ) => void;
  remove: () => void;
};

type MktoForms2Api = {
  loadForm: (
    baseUrl: string,
    munchkinId: string,
    formId: number,
    callback: (form: MktoFormInstance) => void,
  ) => void;
  whenReady: (callback: (form: MktoFormInstance) => void) => void;
};

type MunchkinApi = {
  init: (munchkinId: string) => void;
};

declare global {
  interface Window {
    MktoForms2?: MktoForms2Api;
    Munchkin?: MunchkinApi;
  }
}

const initMunchkin = (munchkinId: string) => {
  if (window.Munchkin) {
    window.Munchkin.init(munchkinId);
  }
};

const loadMunchkinScript = () => {
  if (window.Munchkin) {
    return Promise.resolve();
  }

  return loadScript({ src: '//munchkin.marketo.net/munchkin.js' });
};

const loadMarketoFormScript = ({ baseUrl }: { baseUrl: string }) => {
  if (window.MktoForms2) {
    return Promise.resolve();
  }

  return loadScript({
    src: baseUrl + '/js/forms2/js/forms2.min.js',
    async: false,
  });
};

/**
 * Loads a hidden refer a friend form post trial signup submission and submits it to the refer a friend provider.
 */
export const ReferAFriend = ({
  email,
  firstName,
  referralId,
}: ReferAFriendProps) => {
  useEffect(() => {
    const initializeMunchkin = async () => {
      try {
        await loadMunchkinScript();
        initMunchkin(MUNCHKIN_ID);
      } catch (error) {
        console.error('Error loading Munchkin script:', error);
      }
    };

    const initializeForm = async () => {
      try {
        await loadMarketoFormScript({ baseUrl: BASE_URL });

        if (!window.MktoForms2) {
          return;
        }

        window.MktoForms2.loadForm(
          BASE_URL,
          MUNCHKIN_ID,
          MARKETO_FORM_ID,
          (form) => {
            form.vals({
              Email: email,
              FirstName: firstName,
              AP_Referrer_ID__c_contact: referralId,
            });

            form.submit();

            // Prevent redirecting to Marketo follow-up URL.
            form.onSuccess((_values, _followUpUrl) => {
              return false;
            });
          },
        );
      } catch (error) {
        console.error('Error loading Marketo form:', error);
      }
    };

    initializeMunchkin();
    initializeForm();

    return () => {
      if (window.MktoForms2) {
        window.MktoForms2.whenReady((form) => {
          form.remove();
        });
      }
    };
  }, []);

  return <form id={'mktoForm_' + MARKETO_FORM_ID} className="hidden" />;
};
